import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui/Button';
import { useGetPlaylistLayerContent } from '../../../queries';
import { useDeletePlaylistContent } from '../../../queries/playlistContent';
import { usePlaylistPublishImpact } from '../../../queries/showPage';
import { useCustomToast } from '../../Contexts/ToastContext';
import { ackSharedPlaylistEdit, hasAckedSharedPlaylistEdit } from './playlistEditAckStore';
import MediaThumb from './components/MediaThumb';
import BuilderDuration from './components/BuilderDuration';
import { useMediaPreview, isPreviewable } from '../MediaPreview/MediaPreviewContext';

const SHOW = 6;

/**
 * Every real playlist row (picked/shared playlists AND Fast-Create
 * "Auto-created" playlists) in either layer — pixel-matched from
 * .claude/pp-campaign-builder-prototype.html's v14 `.pl`/`.pl-tg`/`.pl-body`/
 * `.pl-list`/`.pl-note`/`.sub-it`/`.pl-more`, renamed to this app's `pp-cb-*`
 * convention. Shared between CampaignCard.jsx (MAIN) and
 * CampaignVibesSection.jsx (PLAYGROUND/"الصور-أجواء") so both layers get the
 * identical accordion mechanics — header actions differ per layer and are
 * supplied by the caller via `headerActions`.
 *
 * Replaces BOTH of VibesPlaylistRow's old branches (the `isOwnAutoPlaylist`
 * inline-flatten render AND the shared/other collapsed-row-that-navigates-
 * away render) and CampaignCard.jsx's own navigate-to-`/playlist/:id/show`
 * chevron — the legacy playlist page itself is untouched and still reachable
 * from the library, this just removes the BUILDER's own link into it (spec
 * item C).
 *
 * Sub-items are fetched UNCONDITIONALLY (not lazily on first expand) via the
 * same `GET /playlist/{id}/{layer}/media` endpoint the legacy page's
 * `useGetPlaylistLayerContent` already uses — there is no item-count field on
 * the campaign-layer-content response, so the header's "N عنصر/صورة" count
 * and the "عرض الكل (N)" label are both derived from this fetch's own
 * `.length`, and its own isLoading/isError+refetch ARE this row's skeleton/
 * error states (spec item A/#2).
 */
function PlaylistAccordionRow({
  row,
  layer,
  campaignId,
  open,
  onToggleOpen,
  gripNode,
  thumbNode,
  metaKind = 'item', // 'item' | 'image' — picks the i18n count wording
  showMutedSuffix = false,
  headerActions,
  extraBodyNode, // optional (items, isLoading) => ReactNode, rendered above the sub-item list
  allDurationsEditable = false,
  toastTitle,
}) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [confirm, setConfirm] = useState(null); // { proceed } | null

  const { data: impact } = usePlaylistPublishImpact(row.playlist_id);
  const campaignCount = impact?.campaign_count ?? 1;
  const isShared = campaignCount > 1;

  const { isLoading, data, isError, refetch } = useGetPlaylistLayerContent(row.playlist_id, layer);
  const items = Array.isArray(data) ? data : [];
  const visible = showAll ? items : items.slice(0, SHOW);

  const deletePlaylistContent = useDeletePlaylistContent();

  // v15 media preview — this accordion's own sub-items are their own
  // navigation scope (task spec: "a playlist accordion's own sub-items —
  // each is its own navigation scope, don't cross between them"). Unlike
  // the sheet-grid's media tab, rows CAN include audio in the nav list.
  const mediaPreview = useMediaPreview();
  const openSubPreview = (sub) => {
    const visualItems = items.filter(isPreviewable);
    const idx = visualItems.indexOf(sub);
    mediaPreview.openLightbox(visualItems, idx < 0 ? 0 : idx, { stopAudioOnClose: true });
  };

  // Spec item E: first edit (delete OR duration-change) on a SHARED playlist
  // this page-session opens a confirm dialog; every later edit on the SAME
  // playlist this session skips it. Non-shared playlists never gate.
  const requestEdit = (proceed) => {
    if (!isShared || hasAckedSharedPlaylistEdit(row.playlist_id)) {
      proceed();
      return;
    }
    setConfirm({ proceed });
  };

  // Spec item G: every sub-item edit/removal must flip the campaign's own
  // publish-dirty banner immediately, via the SAME invalidation key
  // (['getCampaign', campaignId]) the rest of the builder already uses — not
  // a new/parallel mechanism.
  const afterEdit = () => {
    queryClient.invalidateQueries(['getCampaign', campaignId]);
  };

  const deleteSubItem = async (sub) => {
    await deletePlaylistContent.mutateAsync({
      playlist_id: row.playlist_id,
      playlist_media_id: sub.playlist_media_id,
    });
    await refetch();
    afterEdit();
    showToast('success', toastTitle, t('dragdrop.playlist_item_removed_toast', { name: sub.file_name || sub.title }));
  };

  return (
    <div className={`pp-cb-pl${open ? ' open' : ''}`}>
      <div className="pp-cb-item">
        {gripNode}
        <button
          type="button"
          className="pp-cb-pl-tg"
          aria-expanded={open}
          onClick={onToggleOpen}
          title={open ? t('dragdrop.playlist_fold') : t('dragdrop.playlist_unfold')}
        >
          <div className="pp-cb-item-thumb">{thumbNode}</div>
          <div className="pp-cb-item-txt">
            <span className="n">{row.playlist_title}</span>
            <span className="m">
              {isLoading
                ? t('dragdrop.playlist_label')
                : t(`dragdrop.playlist_meta_count_${metaKind}`, { count: items.length })}
              {showMutedSuffix && ` · ${t('show.show_vibes_muted_suffix')}`}
              {isShared && (
                <span className="pp-badge pp-badge--info" style={{ marginInlineStart: 8 }}>
                  <i className="pi pi-clone" style={{ fontSize: 11 }} />
                  {t('show.show_vibes_shared_badge', { count: campaignCount })}
                </span>
              )}
            </span>
          </div>
          <span className="pp-cb-pl-chev"><i className="bx bx-chevron-down" /></span>
        </button>
        <div className="pp-cb-item-actions">{headerActions}</div>
      </div>

      <div className="pp-cb-pl-body">
        <div className="in">
          <div className="pp-cb-pl-list">
            {isShared && (
              <div className="pp-cb-pl-note">
                <i className="pi pi-info-circle" />
                <span>{t('dragdrop.shared_playlist_note', { count: campaignCount })}</span>
              </div>
            )}

            {typeof extraBodyNode === 'function' ? extraBodyNode({ items, isLoading }) : extraBodyNode}

            {isLoading ? (
              [0, 1, 2].map((i) => (
                <div className="pp-cb-sub-sk" key={i}>
                  <div className="b" style={{ width: 34, height: 34, borderRadius: 8 }} />
                  <div style={{ flex: 1 }}>
                    <div className="b" style={{ height: 11, width: '55%' }} />
                  </div>
                </div>
              ))
            ) : isError ? (
              <div className="pp-cb-pl-note err">
                <span>{t('dragdrop.playlist_items_load_error')}</span>
                <button type="button" className="pp-cb-chipbtn" title={t('buttons.retry')} onClick={() => refetch()}>
                  <i className="pi pi-refresh" />
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="pp-cb-pl-note" style={{ color: 'var(--pp-comp-muted)' }}>
                {t('dragdrop.playlist_empty')}
              </div>
            ) : (
              <>
                {visible.map((sub, idx) => {
                  const upType = String(sub.type || '').toUpperCase();
                  const durationEditable = allDurationsEditable || upType === 'IMAGE';
                  return (
                    <div className="pp-cb-sub-it" key={sub.playlist_media_id ?? sub.id}>
                      <span className="ix">{idx + 1}</span>
                      <div className="sth"><MediaThumb item={sub} onPreview={openSubPreview} /></div>
                      <span className="sn">{sub.file_name || sub.title}</span>
                      <span className="sd">
                        <BuilderDuration
                          seconds={sub.custom_duration}
                          editable={durationEditable}
                          source="playlist"
                          id={sub.playlist_media_id}
                          playlistId={row.playlist_id}
                          playgroundLayer={layer}
                          guard={requestEdit}
                          onSaved={afterEdit}
                        />
                      </span>
                      <button
                        type="button"
                        className="pp-cb-chipbtn del"
                        title={t('buttons.delete')}
                        onClick={() => requestEdit(() => deleteSubItem(sub))}
                      >
                        <i className="bx bx-trash" />
                      </button>
                    </div>
                  );
                })}
                {items.length > SHOW && (
                  <button type="button" className="pp-cb-pl-more" onClick={() => setShowAll((s) => !s)}>
                    {showAll ? t('dragdrop.playlist_show_less') : t('dragdrop.playlist_show_all', { count: items.length })}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {confirm && (
        <>
          <div className="pp-cb-cfm-scrim show" onClick={() => setConfirm(null)} />
          <div className="pp-cb-cfm show" role="alertdialog" aria-labelledby="pp-cb-cfm-title">
            <div className="ci"><i className="pi pi-clone" /></div>
            <h4 id="pp-cb-cfm-title">{t('dragdrop.shared_playlist_edit_confirm_title')}</h4>
            <p>{t('dragdrop.shared_playlist_edit_confirm_body', { name: row.playlist_title, count: campaignCount })}</p>
            <div className="ca flex gap-2">
              <Button
                className="flex-1"
                label={t('dragdrop.shared_playlist_edit_confirm_ok')}
                onClick={() => {
                  const { proceed } = confirm;
                  ackSharedPlaylistEdit(row.playlist_id);
                  setConfirm(null);
                  proceed();
                }}
              />
              <Button className="flex-1" outlined label={t('buttons.cancel')} onClick={() => setConfirm(null)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PlaylistAccordionRow;
